import type { ReactNode } from 'react'
import { parseCalendarDate } from '@/lib/calendar-date'
import type { PropertyCalendarOrphanGap } from '@/api/client'
import type { EventBlockSpec } from '@/components/calendar/event-block-meta'

/**
 * The day list: everything the grid draws on one day, as rows that each open
 * the thing's own sheet. The grid shows a day's busiest events as blocks and
 * folds the rest into "+N more"; this is where all of them can be read.
 */

const OPEN_PREFIX = 'day:'
const OPEN_PATTERN = /^day:(\d{4}-\d{2}-\d{2})$/

/** What `?open=` holds for the day list: `day:<YYYY-MM-DD>`. */
export function dayOpenValue(day: string): string {
  return `${OPEN_PREFIX}${day}`
}

/** The day, or null for anything that is not a real date: `day:2026-02-30` opens nothing. */
export function parseDayOpen(raw: string | null): string | null {
  const match = raw ? OPEN_PATTERN.exec(raw) : null
  return match && parseCalendarDate(match[1]) ? match[1] : null
}

export type DayListGroupId =
  | 'stays'
  | 'cleanings'
  | 'deposits'
  | 'supplies'
  | 'reviews'
  | 'gaps'
  | 'blocks'
  | 'reviews-owed'
  | 'reviews-thank-you'
  | 'reviews-untracked'

export interface DayListRow {
  key: string
  /** Colours of the small icon tile: the same classes the thing's bar or block wears. */
  tileClassName: string
  icon: ReactNode
  title: string
  detail: string | null
  /** The row button's accessible name: what it is and what it opens. */
  ariaLabel: string
  testId: string
  /** `data-*` attributes for tests. */
  data?: Record<string, string>
  /** Left out for a row with no sheet of its own (a block): it is shown, not tapped. */
  onOpen?: () => void
  /**
   * A second way out of the row, shown under it: a link to another page (the
   * Reviews page's draft for this stay). Navigation only, like the row.
   */
  link?: { label: string; to: string; testId: string }
}

export interface DayListGroup {
  id: DayListGroupId
  heading: string
  /** A sentence under the heading, for what the group's rows do not say on their own. */
  note?: string
  rows: DayListRow[]
}

/** The rows of the groups that have any, in the order the sheet lists them. */
export function nonEmptyGroups(groups: DayListGroup[]): DayListGroup[] {
  return groups.filter(group => group.rows.length > 0)
}

/** Whether `day` is one of the gap's nights. */
export function gapCoversDay(gap: PropertyCalendarOrphanGap, day: string): boolean {
  return gap.first_night <= day && day <= gap.last_night
}

/** Event blocks as rows: the same icon, words and colours as the block, plus the time. */
export function eventRows(
  blocks: EventBlockSpec[],
  icon: (spec: EventBlockSpec) => ReactNode
): DayListRow[] {
  return blocks.map(spec => ({
    key: spec.key,
    tileClassName: spec.meta.classes,
    icon: icon(spec),
    title: spec.meta.words,
    detail: spec.detail,
    ariaLabel: `${spec.meta.words}${spec.detail ? `, ${spec.detail}` : ''} - ${spec.action}`,
    testId: `day-list-${spec.layer}-row`,
    data: spec.data,
    onOpen: spec.onOpen,
  }))
}
